import { api } from "../../services/ApiService.js";
import FileUploader from "./FileUploader.js";
import CustomSelect from "./CustomSelect.js";
import InputMask from "./InputMask.js";

export default class FormCalcCost {
    /**
     * @param {Object} options
     * @param {string} options.formElement - Form element
     */
    constructor(options = {}) {
        this.form = options.form;
        this.formHandler = options.formHandler;
        if (!this.form) return;

        // DOM Elements
        this.categoriesContainer = this.form.querySelector(".js-services-category-container");
        this.servicesContainer = this.form.querySelector(".js-services-container");
        this.dynamicFieldsContainer = this.form.querySelector(".js-dynamic-fields-container");

        // UI Modules
        this.fileUploaders = [];
    }

    /**
     * Initialization
     */
    init() {
        if (!this.form) return;

        // Bind events
        this.bindEvents();

        // First time initialization
        this.initUIComponents(this.form);
    }

    /**
     * Bind events
     */
    bindEvents() {
        this.form.addEventListener("change", async (e) => {
            // Category change event
            const categoryTrigger = e.target.closest(".js-trigger-category");
            if (categoryTrigger) {
                await this.handleCategoryChange(categoryTrigger.value);
                return;
            }

            // Service change event
            const serviceTrigger = e.target.closest(".js-trigger-service");
            if (serviceTrigger) {
                await this.handleServiceChange(serviceTrigger.value);
                return;
            }
        });
    }

    /**
     * Handles category change REST request
     *
     * @param {string|number} categoryId
     * @returns {Promise<void>}
     */
    async handleCategoryChange(categoryId) {
        if (!this.servicesContainer) return;

        this.formHandler?.setLoadingState(this.form, true);

        try {
            const response = await api.calcCostServices(categoryId);
            this.updateContainer(this.servicesContainer, response?.data?.html);
            this.updateContainer(this.dynamicFieldsContainer, "");
            this.initUIComponents(this.servicesContainer);

            this.formHandler?.refreshValidator(this.form);
        } catch (error) {
            console.error("Error loading services:", error);
        } finally {
            this.formHandler?.setLoadingState(this.form, false);
        }
    }

    /**
     * Handles service change REST request
     *
     * @param {string|number} serviceId
     * @returns {Promise<void>}
     */
    async handleServiceChange(serviceId) {
        if (!this.dynamicFieldsContainer) return;

        this.formHandler?.setLoadingState(this.form, true);

        try {
            const response = await api.calcCostFields(serviceId);
            this.updateContainer(this.dynamicFieldsContainer, response?.data?.html);
            this.initUIComponents(this.dynamicFieldsContainer);

            this.formHandler?.refreshValidator(this.form);
        } catch (error) {
            console.error("Error loading dynamic fields:", error);
        } finally {
            this.formHandler?.setLoadingState(this.form, false);
        }
    }

    /**
     * Безпечне оновлення HTML зі знищенням старих інстансів компонентів.
     */
    updateContainer(container, html) {
        if (!container) return;

        CustomSelect.destroyWithin(container);
        InputMask.destroyWithin(container);

        const cleanHtml = html ? html.trim() : "";
        container.innerHTML = cleanHtml;
        container.style.display = cleanHtml ? "" : "none";
    }

    /**
     * Initializes UI components
     * @param {HTMLElement} scope - DOM scope
     */
    initUIComponents(scope = this.form) {
        if (!scope) return;

        // Custom selects
        CustomSelect.selectsInit(scope);

        // Inputmask
        InputMask.masksInit(scope);

        // File uploader
        const fileContainers = scope.querySelectorAll(".form-field-file-upload");
        if (fileContainers.length) {
            fileContainers.forEach((container) => {
                if (!container.dataset.uploaderInitialized) {
                    const uploader = new FileUploader(container);
                    this.fileUploaders.push(uploader);
                    container.dataset.uploaderInitialized = "true";
                }
            });
        }
    }
}
